import React from "react";
import type { CSSProperties } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextField } from "@material-ui/core";
import {
  filterProductOptions,
  formatProductOptionLabel,
  type ProductOptionLike,
} from "../../utils/productOptions";

interface ProductAutocompleteProps {
  /** Complete product catalog of the supplier. */
  products: ProductOptionLike[];
  /** Selected product id; 0 means "no selection". */
  value: number;
  onChange: (productId: number) => void;
  /** Accessible name of the input, e.g. `product-id-0`. */
  inputAriaLabel: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Searchable product selector: the search lives inside the field, so the user
 * types the product name or line and the list narrows while keeping the current
 * selection visible.
 */
export function ProductAutocomplete({
  products,
  value,
  onChange,
  inputAriaLabel,
  label = "Producto",
  placeholder = "Buscar producto…",
  disabled = false,
  className,
  style,
}: ProductAutocompleteProps) {
  const selected = products.find((product) => product.id === value) ?? null;

  return (
    <Autocomplete
      options={products}
      getOptionLabel={formatProductOptionLabel}
      value={selected}
      onChange={(_event, option) => onChange(option ? option.id : 0)}
      filterOptions={(options, { inputValue }) =>
        filterProductOptions(options, inputValue)
      }
      disabled={disabled}
      size="small"
      className={className}
      style={style}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          inputProps={{ ...params.inputProps, "aria-label": inputAriaLabel }}
        />
      )}
    />
  );
}

export default ProductAutocomplete;
